import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Loader2, ShieldCheck, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function FirstPasswordChange() {
  const navigate = useNavigate();
  const { logout, refresh } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!oldPw || !newPw || !confirmPw) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        old_password: oldPw, new_password: newPw, confirm_password: confirmPw,
      });
      await refresh();
      toast.success("Mot de passe défini. Bienvenue !");
      navigate("/parent", { replace: true });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-[#FAFAFA] px-4 dark:bg-[#0A0A0A]">
      <motion.div
        initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-md"
      >
        <div className="mb-6 flex items-center gap-2.5">
          <Logo size={34} />
          <span className="text-base font-semibold tracking-tight text-slate-900 dark:text-slate-100">Le Passeport</span>
        </div>

        <div className="rounded-2xl border border-slate-100 bg-white p-6 shadow-[0_4px_20px_rgb(0,0,0,0.04)] dark:border-white/10 dark:bg-white/[0.03]" data-testid="first-password-card">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-sky-50 text-sky-500 dark:bg-sky-500/10">
            <ShieldCheck size={24} />
          </div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">Définissez votre mot de passe</h1>
          <p className="mt-1 mb-5 text-sm text-slate-500">
            Pour votre sécurité, veuillez remplacer le mot de passe temporaire fourni par l'enseignant.
          </p>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="old">Mot de passe temporaire actuel</Label>
              <Input id="old" type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} data-testid="first-old-password" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="new">Nouveau mot de passe</Label>
              <Input id="new" type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="first-new-password" />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="confirm">Confirmer le nouveau mot de passe</Label>
              <Input id="confirm" type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} data-testid="first-confirm-password" />
            </div>
          </div>

          <Button onClick={submit} disabled={busy} className="mt-5 w-full rounded-xl" data-testid="first-password-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <><KeyRound size={16} className="mr-2" /> Définir mon mot de passe</>}
          </Button>

          <button
            onClick={logout}
            className="mt-4 flex w-full items-center justify-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-slate-600"
            data-testid="first-password-logout"
          >
            <LogOut size={14} /> Se déconnecter
          </button>
        </div>
      </motion.div>
    </div>
  );
}
